/**
 * HPQ Search Dropdown — Intelligent search overlay for the nav search proxies.
 *
 * Attaches to the search inputs exported by hpq-nav.js after the
 * 'hpq:search-proxy-ready' event fires.
 *
 * Sections displayed:
 *   1. Auntie Fu suggestions   — on focus, before typing
 *   2. Popular products         — on focus, from embedded #hpq-catalog-data
 *   3. Live autocomplete        — on type, replaces 1+2 (Shopify predictive search)
 *
 * Intercepts Stiletto's [data-search] click handler so the native overlay
 * does not open when the HPQ dropdown is active.
 */
(function () {
  'use strict';

  // ──────────────────────────────────────────────
  // Constants
  // ──────────────────────────────────────────────

  var AUNTIE_SUGGESTIONS = [
    {
      text: 'What goes with dumplings?',
      icon: '🥟',
      handles: ['crunchy-garlic-chili-crisp', 'toasted-seed-chili-crunch', 'wild-mushroom-chili-sauce'],
      caption: 'Dumplings want a dipping sauce with a backbone. Spoon one of these into a saucer, don\'t overthink it.'
    },
    {
      text: 'Something warming for spring',
      icon: '🌱',
      handles: ['savory-mushroom-hotpot-soup-base', 'sichuan-mala-hotpot-soup-base', 'roasted-chili-noodles'],
      caption: 'Spring still bites at night. Make a bowl that sits you down for twenty minutes.'
    },
    {
      text: 'Quick weeknight dinner ideas',
      icon: '⚡',
      handles: ['spicy-hotpot-thick-cut-noodles', 'spicy-sesame-dan-dan-noodles', 'roasted-chili-noodles'],
      caption: 'Four minutes, one pot. Auntie\'s seen you eat standing up — pick one of these and actually sit.'
    },
    {
      text: 'Best sauce for noodles',
      icon: '🍜',
      handles: ['toasted-seed-chili-crunch', 'wild-mushroom-chili-sauce', 'crunchy-garlic-chili-crisp'],
      caption: 'A spoonful at the end, not the beginning. Stir it through while the noodles are still hot.'
    },
    {
      text: 'I need something spicy',
      icon: '🌶️',
      handles: ['sichuan-mala-hotpot-soup-base', 'spicy-hotpot-thick-cut-noodles', 'toasted-seed-chili-crunch'],
      caption: 'Auntie doesn\'t argue with you. Real heat — the numbing kind — starts here.'
    },
    {
      text: 'Meal for two under $30',
      icon: '💰',
      handles: ['spicy-hotpot-thick-cut-noodles', 'savory-mushroom-hotpot-soup-base', 'spicy-sesame-dan-dan-noodles'],
      caption: 'One soup base, one pack of noodles. That\'s dinner. Add a sauce if you\'re showing off.'
    }
  ];

  var DEBOUNCE_MS = 250;
  var MAX_POPULAR = 4;
  var MAX_RESULTS = 6;
  var IDLE_SUGGESTION_COUNT = 2;

  // Auntie lines for the on-brand empty state (rotated at random).
  var AUNTIE_EMPTY_LINES = [
    "Auntie doesn't have that one — but she pulled a few things off her table for you.",
    "Not on her shelf. Try one of these instead.",
    "Hmm. That word doesn't ring a bell. Take a look at what she makes."
  ];

  // ──────────────────────────────────────────────
  // State
  // ──────────────────────────────────────────────

  // Flag used by the [data-search] capture interceptor
  window.hpqSearchDropdownActive = true;

  // ──────────────────────────────────────────────
  // Utilities
  // ──────────────────────────────────────────────

  function debounce(fn, wait) {
    var timer;
    return function () {
      var args = arguments;
      clearTimeout(timer);
      timer = setTimeout(function () {
        fn.apply(null, args);
      }, wait);
    };
  }

  function fmtPrice(cents) {
    var d = Math.floor(cents / 100);
    var r = cents % 100;
    return '$' + d + '.' + (r < 10 ? '0' + r : String(r));
  }

  function getCatalog() {
    var el = document.getElementById('hpq-catalog-data');
    if (!el) return [];
    try {
      return JSON.parse(el.textContent) || [];
    } catch (e) {
      return [];
    }
  }

  function getPredictiveSearchUrl() {
    return (window.theme && window.theme.routes && window.theme.routes.predictive_search_url) ||
           '/search/suggest';
  }

  function pickRandom(arr, n) {
    var copy = arr.slice();
    var picks = [];
    while (picks.length < n && copy.length) {
      var idx = Math.floor(Math.random() * copy.length);
      picks.push(copy.splice(idx, 1)[0]);
    }
    return picks;
  }

  function localCatalogSearch(term) {
    var lower = (term || '').toLowerCase().trim();
    if (!lower) return [];
    var catalog = getCatalog();
    return catalog.filter(function (p) {
      var t = (p.title || '').toLowerCase();
      var ty = (p.type || '').toLowerCase();
      return t.indexOf(lower) !== -1 || ty.indexOf(lower) !== -1;
    });
  }

  function findProductsByHandles(catalog, handles) {
    var results = [];
    var seen = {};
    handles.forEach(function (handle) {
      for (var i = 0; i < catalog.length; i++) {
        var product = catalog[i];
        if (seen[product.id]) continue;
        var url = product.url || '';
        var match = url.match(/\/products\/([^\/]+)\/?$/);
        if (match && match[1] === handle) {
          results.push(product);
          seen[product.id] = true;
          break;
        }
      }
    });
    return results;
  }

  // ──────────────────────────────────────────────
  // DOM builders
  // ──────────────────────────────────────────────

  function buildDropdown() {
    var el = document.createElement('div');
    el.className = 'hpq-search-dropdown';
    el.setAttribute('role', 'listbox');
    el.setAttribute('id', 'hpq-search-dropdown');
    return el;
  }

  function buildSectionHeader(label) {
    var h = document.createElement('div');
    h.className = 'hpq-search-dropdown__section-header';
    h.textContent = label;
    return h;
  }

  function buildSuggestionItem(suggestion, onClick) {
    var item = document.createElement('button');
    item.className = 'hpq-search-dropdown__item hpq-search-dropdown__item--suggestion';
    item.setAttribute('type', 'button');
    item.setAttribute('role', 'option');
    item.setAttribute('tabindex', '-1');

    var icon = document.createElement('span');
    icon.className = 'hpq-search-dropdown__item-icon';
    icon.setAttribute('aria-hidden', 'true');
    icon.textContent = suggestion.icon;

    var text = document.createElement('span');
    text.className = 'hpq-search-dropdown__item-text';
    text.textContent = suggestion.text;

    item.appendChild(icon);
    item.appendChild(text);

    // Prevent focus transfer from the input on mousedown — if the input loses focus,
    // the wrap's focusout handler (setTimeout) will hide the dropdown after innerHTML
    // is cleared, and the new curated results render into a hidden dropdown.
    item.addEventListener('mousedown', function (e) {
      e.preventDefault();
    });

    item.addEventListener('click', function () {
      onClick(suggestion.text);
    });

    return item;
  }

  function buildProductItem(product, onClick) {
    var item = document.createElement('button');
    item.className = 'hpq-search-dropdown__item hpq-search-dropdown__item--product';
    item.setAttribute('type', 'button');
    item.setAttribute('role', 'option');
    item.setAttribute('tabindex', '-1');

    if (product.image) {
      var img = document.createElement('img');
      img.className = 'hpq-search-dropdown__item-image';
      img.src = product.image;
      img.alt = product.title;
      img.loading = 'lazy';
      img.width = 40;
      img.height = 40;
      item.appendChild(img);
    } else {
      var imgPlaceholder = document.createElement('div');
      imgPlaceholder.className = 'hpq-search-dropdown__item-image hpq-search-dropdown__item-image--placeholder';
      item.appendChild(imgPlaceholder);
    }

    var info = document.createElement('div');
    info.className = 'hpq-search-dropdown__item-info';

    var title = document.createElement('div');
    title.className = 'hpq-search-dropdown__item-text';
    title.textContent = product.title;

    var price = document.createElement('div');
    price.className = 'hpq-search-dropdown__item-price';
    price.textContent = fmtPrice(product.price);

    info.appendChild(title);
    info.appendChild(price);
    item.appendChild(info);

    item.addEventListener('click', function () {
      onClick(product.url);
    });

    return item;
  }

  function buildLoadingState() {
    var el = document.createElement('div');
    el.className = 'hpq-search-dropdown__loading';
    el.textContent = 'Searching...';
    return el;
  }

  function buildNoResults() {
    var el = document.createElement('div');
    el.className = 'hpq-search-dropdown__no-results';
    el.textContent = 'No results found.';
    return el;
  }

  function buildAuntieEmpty() {
    var el = document.createElement('div');
    el.className = 'hpq-search-dropdown__empty';
    el.textContent = AUNTIE_EMPTY_LINES[Math.floor(Math.random() * AUNTIE_EMPTY_LINES.length)];
    return el;
  }

  // ──────────────────────────────────────────────
  // Render helpers
  // ──────────────────────────────────────────────

  function renderIdleContent(dropdown, inputEl) {
    dropdown.innerHTML = '';

    // Section 1: Popular products first
    var catalog = getCatalog();
    if (catalog.length > 0) {
      dropdown.appendChild(buildSectionHeader('Products'));
      var shown = Math.min(MAX_POPULAR, catalog.length);
      for (var i = 0; i < shown; i++) {
        var product = catalog[i];
        dropdown.appendChild(buildProductItem(product, function (url) {
          window.location.href = url;
        }));
      }
    }

    // Section 2: Random Auntie suggestions
    dropdown.appendChild(buildSectionHeader("From Auntie's table"));
    var picks = pickRandom(AUNTIE_SUGGESTIONS, IDLE_SUGGESTION_COUNT);
    picks.forEach(function (suggestion) {
      dropdown.appendChild(buildSuggestionItem(suggestion, function () {
        inputEl.value = '';
        renderCuratedResults(dropdown, inputEl, suggestion);
      }));
    });
  }

  function renderSearchResults(dropdown, products) {
    dropdown.innerHTML = '';

    if (!products || products.length === 0) {
      dropdown.appendChild(buildNoResults());
      return;
    }

    dropdown.appendChild(buildSectionHeader('Products'));
    products.forEach(function (product) {
      dropdown.appendChild(buildProductItem(product, function (url) {
        window.location.href = url;
      }));
    });
  }

  function renderCuratedResults(dropdown, inputEl, suggestion) {
    dropdown.innerHTML = '';
    var catalog = getCatalog();
    var products = findProductsByHandles(catalog, suggestion.handles);

    if (!products || products.length === 0) {
      dropdown.appendChild(buildNoResults());
      return;
    }

    if (suggestion.caption) {
      var caption = document.createElement('div');
      caption.className = 'hpq-search-dropdown__caption';
      caption.textContent = suggestion.caption;
      dropdown.appendChild(caption);
    }

    dropdown.appendChild(buildSectionHeader(suggestion.text));

    products.forEach(function (product) {
      dropdown.appendChild(buildProductItem(product, function (url) {
        window.location.href = url;
      }));
    });
  }

  function renderEmptySearchResults(dropdown, term) {
    dropdown.innerHTML = '';
    dropdown.appendChild(buildAuntieEmpty());
    var catalog = getCatalog();
    if (catalog.length > 0) {
      dropdown.appendChild(buildSectionHeader('Maybe one of these'));
      var shown = Math.min(MAX_POPULAR, catalog.length);
      for (var i = 0; i < shown; i++) {
        var product = catalog[i];
        dropdown.appendChild(buildProductItem(product, function (url) {
          window.location.href = url;
        }));
      }
    }
  }

  // ──────────────────────────────────────────────
  // Live search (Shopify predictive search API)
  // ──────────────────────────────────────────────

  function triggerSearch(dropdown, inputEl, term) {
    if (!term || !term.trim()) {
      renderIdleContent(dropdown, inputEl);
      return;
    }

    // Show loading state immediately (no debounce — this is a deliberate click)
    dropdown.innerHTML = '';
    dropdown.appendChild(buildLoadingState());

    fetchJsonSearch(term)
      .then(function (products) {
        if (!dropdown.classList.contains('is-active')) return;
        var mapped = products.map(function (p) {
          return {
            title: p.title,
            price: p.price,
            image: p.image,
            url: p.url
          };
        });
        if (mapped.length === 0) {
          var local = localCatalogSearch(term);
          if (local.length > 0) {
            renderSearchResults(dropdown, local);
          } else {
            renderEmptySearchResults(dropdown, term);
          }
        } else {
          renderSearchResults(dropdown, mapped);
        }
      })
      .catch(function () {
        if (!dropdown.classList.contains('is-active')) return;
        var local = localCatalogSearch(term);
        if (local.length > 0) {
          renderSearchResults(dropdown, local);
        } else {
          renderEmptySearchResults(dropdown, term);
        }
      });
  }

  function fetchJsonSearch(term) {
    var url = '/search/suggest.json' +
      '?q=' + encodeURIComponent(term.trim()) +
      '&resources[type]=product&resources[limit]=' + MAX_RESULTS;

    return fetch(url)
      .then(function (res) {
        if (!res.ok) throw new Error('JSON search failed: ' + res.status);
        return res.json();
      })
      .then(function (data) {
        return (data && data.resources && data.resources.results && data.resources.results.products) || [];
      });
  }

  var debouncedSearch = debounce(function (dropdown, inputEl, term) {
    fetchJsonSearch(term)
      .then(function (products) {
        if (!dropdown.classList.contains('is-active')) return;
        var mapped = products.map(function (p) {
          return {
            title: p.title,
            price: p.price,
            image: p.image,
            url: p.url
          };
        });
        if (mapped.length === 0) {
          var local = localCatalogSearch(term);
          if (local.length > 0) {
            renderSearchResults(dropdown, local);
          } else {
            renderEmptySearchResults(dropdown, term);
          }
        } else {
          renderSearchResults(dropdown, mapped);
        }
      })
      .catch(function () {
        if (!dropdown.classList.contains('is-active')) return;
        var local = localCatalogSearch(term);
        if (local.length > 0) {
          renderSearchResults(dropdown, local);
        } else {
          renderEmptySearchResults(dropdown, term);
        }
      });
  }, DEBOUNCE_MS);

  // ──────────────────────────────────────────────
  // Show / hide dropdown
  // ──────────────────────────────────────────────

  function showDropdown(dropdown, inputEl) {
    renderIdleContent(dropdown, inputEl);
    dropdown.classList.add('is-active');
    inputEl.setAttribute('aria-expanded', 'true');
  }

  function hideDropdown(dropdown, inputEl) {
    dropdown.classList.remove('is-active');
    if (inputEl) inputEl.setAttribute('aria-expanded', 'false');
  }

  // ──────────────────────────────────────────────
  // Keyboard navigation
  // ──────────────────────────────────────────────

  function handleKeyNav(e, dropdown, inputEl) {
    if (!dropdown.classList.contains('is-active')) return;

    var items = dropdown.querySelectorAll('.hpq-search-dropdown__item');
    if (!items.length) return;

    var highlighted = dropdown.querySelector('.hpq-search-dropdown__item.is-highlighted');
    var idx = -1;
    for (var i = 0; i < items.length; i++) {
      if (items[i] === highlighted) { idx = i; break; }
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (highlighted) highlighted.classList.remove('is-highlighted');
      var next = idx < items.length - 1 ? idx + 1 : 0;
      items[next].classList.add('is-highlighted');
      items[next].scrollIntoView({ block: 'nearest' });

    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (highlighted) highlighted.classList.remove('is-highlighted');
      var prev = idx > 0 ? idx - 1 : items.length - 1;
      items[prev].classList.add('is-highlighted');
      items[prev].scrollIntoView({ block: 'nearest' });

    } else if (e.key === 'Enter' && highlighted) {
      e.preventDefault();
      highlighted.click();

    } else if (e.key === 'Escape') {
      hideDropdown(dropdown, inputEl);
      inputEl.blur();
    }
  }

  // ──────────────────────────────────────────────
  // attachDropdown
  // Wires up focus/blur/input/keydown on a single input element.
  // ──────────────────────────────────────────────

  function attachDropdown(inputEl) {
    var dropdown = buildDropdown();
    // Position relative to the proxy wrapper (set by hpq-nav.js inline style)
    var wrap = inputEl.parentElement;
    wrap.style.position = 'relative';
    wrap.appendChild(dropdown);

    inputEl.addEventListener('focus', function () {
      showDropdown(dropdown, inputEl);
    });

    wrap.addEventListener('focusout', function (e) {
      if (wrap.contains(e.relatedTarget)) return;
      setTimeout(function () {
        if (wrap.contains(document.activeElement)) return;
        hideDropdown(dropdown, inputEl);
      }, 0);
    });

    inputEl.addEventListener('input', function () {
      var term = inputEl.value;
      if (!term || !term.trim()) {
        renderIdleContent(dropdown, inputEl);
        return;
      }
      // Show loading indicator immediately, then debounce the actual fetch
      dropdown.innerHTML = '';
      dropdown.appendChild(buildLoadingState());
      debouncedSearch(dropdown, inputEl, term);
    });

    inputEl.addEventListener('keydown', function (e) {
      handleKeyNav(e, dropdown, inputEl);
    });
  }

  // ──────────────────────────────────────────────
  // Intercept Stiletto [data-search] overlay
  // Prevent the native search overlay from opening while our dropdown is active.
  // ──────────────────────────────────────────────

  document.addEventListener('click', function (e) {
    if (window.hpqSearchDropdownActive && document.querySelector('.hpq-search-dropdown.is-active')) {
      var target = e.target && typeof e.target.closest === 'function' && e.target.closest('[data-search]');
      if (target) {
        e.stopPropagation();
        e.preventDefault();
      }
    }
  }, true);

  // ──────────────────────────────────────────────
  // Init
  // ──────────────────────────────────────────────

  function init() {
    var inputs = window.hpqNav && window.hpqNav.searchInputs;
    if (!inputs) {
      document.addEventListener('hpq:search-proxy-ready', init, { once: true });
      return;
    }

    if (inputs.desktop) attachDropdown(inputs.desktop);
    if (inputs.mobile) attachDropdown(inputs.mobile);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
